import React, {Component} from 'react';
import {connect} from 'react-redux';
import {cloneDeep} from 'lodash';
import {Card, Grid, Button, Icon, Table, Modal, Popup, Input} from 'semantic-ui-react';
import {headerCellTitlesOfData, headerCellTitlesOfControl} from '../static/commonStructures';
import {checkPartnoInDatabase, checkRepeatedPrimaryKey, isRowInvalid, formatElement} from '../../utils';
import {hasAccessPermission} from '../../../common/permissions/validations';
import {resources, scopes} from '../../../common/permissions/dictionary';
import {paths} from '../../../common/dictionaries/urls';
import {Actions} from '../../../common/actions';
import ExcelReader from '../../../excelReader/components/index';
import './specialPartsAdd.scss';

const INITIAL_STATE = {
  'tableValues': [],
  'saveButtonDisabled': true,
  'partnoRepeatedInDatabase': new Set(),
};

class SpecialParts extends Component {
  constructor(props) {
    super(props);
    this.handleSaveClick = this.handleSaveClick.bind(this);
    this.handleAddItem = this.handleAddItem.bind(this);
    this.readExcelFile = this.readExcelFile.bind(this);
    this.state = INITIAL_STATE;
  }
  
  componentDidMount() {
    if (!hasAccessPermission(this.props.permissions, resources.SpecialParts, scopes.Create)) {
      this.props.navigateTo(paths.AccessDenied);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.tableValues !== this.state.tableValues) {
      const {tableValues} = this.state;
      tableValues.some((row) => {
        this.setState({'saveButtonDisabled': isRowInvalid(row, tableValues)});
        return isRowInvalid(row, tableValues);
      });
    }
    if (prevProps.specialPart !== this.props.specialPart) {
      const {specialPart} = this.props;
      const updatedPartNoRepeatedInDatabase = new Set(this.state.partnoRepeatedInDatabase);
      if (!specialPart) {
        return;
      }
      const {tableValues} = this.state;
      const tableValuesCopy = cloneDeep(tableValues);
      const value = tableValuesCopy.find((row) => row.partNo === specialPart.code);
      const index = tableValuesCopy.indexOf(value);
      tableValuesCopy[index].name = specialPart.name;
      updatedPartNoRepeatedInDatabase.add(specialPart.partNo);
      this.setState({
        'partnoRepeatedInDatabase': updatedPartNoRepeatedInDatabase,
        'tableValues': tableValuesCopy,
      });
    }
  }

  handleAddItem() {
    const {tableValues} = this.state;
    const tableValuesCopy = cloneDeep(tableValues);
    tableValuesCopy.push({'partNo': '', 'product': '', 'name': ''});
    this.setState({'tableValues': tableValuesCopy});
  }

  handleChangeValue(event, position) {
    const {rowIndex, serializedName} = position;
    const {tableValues} = this.state;
    const deepCopyOfTableValues = cloneDeep(tableValues);
    deepCopyOfTableValues[rowIndex][serializedName] = event.target.value.toUpperCase();
    this.setState({'tableValues': deepCopyOfTableValues});
  }

  async handleSaveClick() {
    const {tableValues} = this.state;
    const {saveSpecialParts, navigateBack} = this.props;
    await saveSpecialParts(tableValues);
    navigateBack();
  }

  readExcelFile(data) {
    const objectsArray = this.getObjectsArray(data.slice(1, data.length));
    this.setState({'tableValues': [...objectsArray]});
    objectsArray.forEach((row) => this.getPartNoRepeatedSet(row));
  }

  getObjectsArray(data) {
    const objectsArray = [];
    data.forEach((element) => {
      const object = {};
      const headerCellsToLoadFromXls = headerCellTitlesOfData.filter((header) => header.serializedName !== 'name');
      headerCellsToLoadFromXls.forEach((header, columnIndex) => {
        object[header.serializedName] = formatElement(element[columnIndex], header);
      });
      objectsArray.push(object);
    });
    return objectsArray;
  }

  handleOnBlur(row, columnName = 'partNo') {
    this.getPartNoRepeatedSet(row, columnName);
    this.getPartDescription(row, columnName);
  }

  getPartNoRepeatedSet(row, columnName = 'partNo') {
    if (columnName === 'partNo') {
      this.props.fetchSpecialPartByPartno(row[columnName] || '__');
    }
  }

  getPartDescription(row, columnName) {
    if (columnName === 'partNo') {
      this.props.fetchSpecialPartByPartno(row[columnName] || '__');
    }
  }

  renderPageHeader() {
    const {saveButtonDisabled} = this.state;
    return (
      <Card raised fluid>
        <Grid.Row>
          <div className='title-bar'>
            <div className='title'>
              <Icon name='arrow left' onClick={this.props.navigateBack}/>
              <h2>Creación de productos especiales</h2>
              <div className='title-bar-actions'>
                <ExcelReader onGetFile={this.readExcelFile}/>
                <Button primary
                  onClick={this.handleSaveClick}
                  disabled={saveButtonDisabled}
                >
                  Guardar
                </Button>
              </div>
            </div>
          </div>
        </Grid.Row>
      </Card>
    );
  }

  renderTable() {
    return (
      <Card fluid raised>
        <Table>
          {this.renderTableHeader()}
          {this.renderTableBody()}
        </Table>
        {this.renderAddItem()}
      </Card>
    );
  }

  renderTableHeader() {
    return (
      <Table.Header>
        <Table.Row>
          {this.renderTableHeaderCells()}
        </Table.Row>
      </Table.Header>
    );
  }

  renderTableHeaderCells() {
    const fieldsToDisplay = headerCellTitlesOfData.concat(headerCellTitlesOfControl);
    return (
      fieldsToDisplay.map((header) => {
        return (
          <Table.HeaderCell key={`header${header.serializedName}`}>
            {header.name}
          </Table.HeaderCell>
        );
      })
    );
  }

  renderTableBody() {
    return (
      <Table.Body>
        {this.renderSpecialParts()}
      </Table.Body>
    );
  }

  renderSpecialParts() {
    const {tableValues} = this.state;
    if (!tableValues) {
      return null;
    }
    return tableValues.map((row, rowIndex) => {
      return (
        <Table.Row key={`row${rowIndex}`}>
          {this.renderInputInCells(row, rowIndex)}
          {this.renderDeleteAction(rowIndex)}
        </Table.Row>
      );
    });
  }

  renderDeleteAction(row) {
    return (
      <Table.Cell textAlign='center' className='modal-cell'>
        <Modal size='mini'
          trigger={
            <span className='link'>
              <Popup
                trigger={
                  <span>
                    <Icon link name='close' color='red' size='large'/>
                  </span>
                }
                content="Remover del listado"
                position='top center'
                inverted
              />
            </span>
          }
          header='¿Desea remover del listado el item seleccionado?'
          actions={[
            {'key': 'cancel', 'content': 'Cancelar'},
            {'key': 'done', 'content': 'Confirmar', 'positive': true,
              'onClick': this.deleteRow.bind(this, row),
              'primary': true,
            },
          ]}
        />
      </Table.Cell>
    );
  }

  deleteRow(row) {
    const {tableValues} = this.state;
    if (tableValues.length === 1) {
      this.setState(INITIAL_STATE);
      return;
    }
    const updatedTableValues = cloneDeep(tableValues);
    updatedTableValues.splice(row, 1);
    this.setState({'tableValues': updatedTableValues});
  }

  renderRowCell(row, title, position) {
    if (title.serializedName === 'name') {
      return row ? row.name : '';
    }
    return (
      <Input
        value={row[title.serializedName]}
        style={{'width': title.width}}
        required={title.required}
        onChange={(event) => this.handleChangeValue(event, position)}
        maxLength={title.maxLength}
        onBlur={this.handleOnBlur.bind(this, row, title.serializedName)}
      />
    );
  }

  renderInputInCells(row, rowIndex) {
    const {partnoRepeatedInDatabase, tableValues} = this.state;
    return (
      headerCellTitlesOfData.map((title, columnIndex) => {
        const position = {'rowIndex': rowIndex, 'serializedName': title.serializedName};
        const duplicatedPrimaryKey = checkRepeatedPrimaryKey(row, tableValues, title.serializedName);
        const partNoExistsInDatabase = checkPartnoInDatabase(row, title, partnoRepeatedInDatabase);
        return (
          <Table.Cell
            textAlign='center'
            key={`element${rowIndex}${columnIndex}`}
            style={{'width': title.width}}
            maxLength={title.maxLength}
          >
            {this.renderRowCell(row, title, position)}
            {this.renderErrorMessage(duplicatedPrimaryKey, 'Número de repuesto repetido')}
            {this.renderErrorMessage(partNoExistsInDatabase, 'Actualizando repuesto especial')}
          </Table.Cell>
        );
      })
    );
  }

  renderErrorMessage(displayErrorMessage, message) {
    if (displayErrorMessage) {
      return this.renderRedAlertMessage(message);
    }
  }

  renderRedAlertMessage(message) {
    return <div className='alert'>{message}</div>;
  }

  renderAddItem() {
    return (
      <div className='addItem' onClick={this.handleAddItem}>
        <Icon name='add'/>
        <strong>AGREGAR REGISTRO</strong>
      </div>
    );
  }

  render() {
    return (
      <div className='special-parts-list'>
        {this.renderPageHeader()}
        {this.renderTable()}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    'specialPart': state.specialParts.specialPart,
    'permissions': state.session.permissions,
  };
};

const {saveSpecialParts, fetchSpecialPartByPartno, navigateTo, navigateBack} = Actions;

export default connect(mapStateToProps, {
  saveSpecialParts,
  fetchSpecialPartByPartno,
  navigateTo,
  navigateBack,
})(SpecialParts);
